import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGeneratePersonas } from '@/hooks/useEvaluator';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from '@/hooks/use-toast';

interface GeneratePersonasDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  onSuccess?: () => void;
}

export function GeneratePersonasDialog({
  open,
  onOpenChange,
  organizationId,
  onSuccess,
}: GeneratePersonasDialogProps) {
  const { currentOrganization } = useOrganization();
  const [datasetName, setDatasetName] = useState('');
  const [count, setCount] = useState(5);
  const generateMutation = useGeneratePersonas();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const trimmed = datasetName.trim();
    if (!trimmed) {
      toast({ title: 'Error', description: 'Population name is required.', variant: 'destructive' });
      return;
    }
    if (count < 1 || count > 20) {
      toast({ title: 'Error', description: 'Count must be between 1 and 20.', variant: 'destructive' });
      return;
    }

    try {
      await generateMutation.mutateAsync({
        dataset_name: trimmed,
        num_personas: count,
        organization_id: organizationId,
      });
      toast({ title: 'Success', description: `Generated ${count} personas in "${trimmed}".` });
      setDatasetName('');
      setCount(5);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate personas.',
        variant: 'destructive',
      });
    }
  };

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setDatasetName('');
      setCount(5);
    }
  };

  const isSubmitting = generateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Generate Personas</DialogTitle>
          <DialogDescription>
            Generate synthetic customer personas to simulate conversations and evaluate your agent.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="dataset-name">Population Name</Label>
              <Input
                id="dataset-name"
                value={datasetName}
                onChange={(e) => setDatasetName(e.target.value)}
                placeholder={`e.g. ${currentOrganization?.name || 'Acme'} Customers`}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="persona-count">Number of Personas</Label>
              <Input
                id="persona-count"
                type="number"
                min={1}
                max={20}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">Between 1 and 20 personas per generation.</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Generating...' : 'Generate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
